/**
 * HeaderSummary Component
 * =======================
 * Display and edit header information with collapsible sections.
 * Mirrors all fields from the original HeaderInfoFourm.
 */

import React, { useState } from 'react';
import fisLogo from '../../logos/fis.svg';

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const NATIONS = [
  "AUS","AUT","BEL","BUL","CAN","CHI","CHN","CRO","CZE","DEN","ESP","EST","FIN","FRA","GBR","GER","GRE","HUN","IRL","ISL","ISR","ITA","JPN","KAZ","KOR","LAT","LIE","LTU","LUX","MEX","MGL","MON","NED","NOR","NZL","POL","POR","ROU","RSA","RUS","SLO","SUI","SVK","SWE","TUR","UKR","USA","UZB",
];

const CATEGORIES = {
  'FIS Race' : 'FIS',
  'US SuperTour' : 'UST',
  'Continental Cup' : 'COC',
  'National Championships' : 'NC',
  'National Junior Championships' : 'NJC',
  'National Ranking List Race': 'NRL',
  'Asia Winter Games' : 'AWG',
  'Australia/New Zealand Cup' : 'ANC',
  'Balkan Cup' : 'BC',
  'Citizen' : 'CIT',
  'Eastern Europe Cup' : 'EEC',
  'European Youth Olympic Festival' : 'EYOF',
  'Far East Cup' : 'FEC',
  'Juniors' : 'JUN',
  'Marathon Cup' : 'MC',
  'Nor-Am Cup' : 'NAC',
  'Olympic Winter Games' : 'OWG',
  'Organisation of Alps Nations' : 'OPA',
  'Scandinavian Cup' : 'SCAN',
  'Slavic Cup' : 'SC',
  'South America Cup' : 'SAC',
  'Under 23' : 'U23',
  'Universiade' : 'UVS',
  'University Race' : 'UNI',
  'World Cup' : 'WC',
  'World Junior Championships' : 'WJC',
  'World Ski Championships' : 'WSC',
  'Youth Olympic Games' : 'YOG',
};

function HeaderSummary({ headerInfo, setHeaderInfo, fisEnabled, ussaEnabled, raceMode }) {
  // General Settings collapsed by default - rarely needs changing
  const [showGeneral, setShowGeneral] = useState(false);
  const [showEvent, setShowEvent] = useState(true);
  const [showRace, setShowRace] = useState(true);
  const [showCourse, setShowCourse] = useState(false);
  const [showFile, setShowFile] = useState(true);

  // Show USSA fields if we're in USSA mode OR if a USSA code is set
  const showUssaFields = raceMode === 'ussa' || ussaEnabled;

  const handleChange = (field, value) => {
    setHeaderInfo((prev) => ({ ...prev, [field]: value }));
  };

  const renderField = (key, label, type = 'text', options = null, placeholder = '') => (
    <div className="field-row" key={key}>
      <label htmlFor={key}>{label}</label>
      {type === 'select' && options ? (
        <select
          id={key}
          value={headerInfo[key] || ''}
          onChange={(e) => handleChange(key, e.target.value)}
        >
          <option value="">Select...</option>
          {Array.isArray(options) 
            ? options.map((opt) => <option key={opt} value={opt}>{opt}</option>)
            : Object.entries(options).map(([name, val]) => (
                <option key={val} value={val}>{name}</option>
              ))
          }
        </select>
      ) : (
        <input
          type={type}
          id={key}
          value={headerInfo[key] || ''}
          onChange={(e) => handleChange(key, e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );

  return (
    <div className="header-summary">
      {/* General Settings */}
      <div className={`section-block ${showGeneral ? 'expanded' : 'collapsed'}`}>
        <div className="section-title" onClick={() => setShowGeneral(!showGeneral)}>
          <span className="toggle-icon">{showGeneral ? '▼' : '▶'}</span>
          General Settings
        </div>
        {showGeneral && (
          <div className="section-content">
            <div className="fields-grid">
              {renderField('sector', 'Discipline Type', 'select', {
                'Cross Country': 'CC',
                'Para Cross Country': 'PCC',
                'Para Snowboard': 'PSB',
                'Para Alpine': 'PAL',
              })}
              {renderField('tempunit', 'Temp Unit', 'select', {
                'Celsius': 'C',
                'Fahrenheit': 'F',
                'Kelvin': 'K',
              })}
              {renderField('longunit', 'Length Unit', 'select', {
                'Meters': 'm',
                'Kilometers': 'km',
                'Miles': 'mi',
              })}
              {renderField('speedunit', 'Speed Unit', 'select', {
                'Kilometers/Hour': 'kmh',
                'Miles/Hour': 'mph',
                'Meters/Second': 'ms',
              })}
              {renderField('windunit', 'Wind Unit', 'select', {
                'Meters/Second': 'ms',
                'Kilometers/Hour': 'kmh',
                'Miles/Hour': 'mph',
                'Knots': 'kn',
              })}
              {renderField('timingBy', 'Timing Company', 'text', null, 'Zone4Systems')}
              {renderField('softWareCompany', 'Software Company', 'text', null, 'Zone4Systems')}
              {renderField('dataProcessingBy', 'Data Processing By', 'text', null, 'Zone4Systems')}
            </div>
          </div>
        )}
      </div>

      {/* Event Info */}
      <div className={`section-block ${showEvent ? 'expanded' : 'collapsed'}`}>
        <div className="section-title" onClick={() => setShowEvent(!showEvent)}>
          <span className="toggle-icon">{showEvent ? '▼' : '▶'}</span>
          Event Info
        </div>
        {showEvent && (
          <div className="section-content">
            <div className="fields-grid">
              {renderField('eventName', 'Event Name')}
              {showUssaFields && renderField('clubName', 'Club Name')}
              {renderField('place', 'Place/Location')}
              {showUssaFields && renderField('state', 'State', 'select', STATES)}
              {renderField('nation', 'Nation', 'select', NATIONS)}
              {renderField('category', 'Category', 'select', CATEGORIES)}
              {showUssaFields && renderField('usedNRLList', '🇺🇸 Used NRL List', 'number')}
              {renderField('usedFISList', <><img src={fisLogo} alt="FIS" className="inline-fis-icon" /> Used FIS List</>, 'number')}
              {renderField('resultType', 'Results Type', 'select', {
                'Startlist': 'Startlist',
                'Partial unofficial result': 'Partial',
                'Unofficial result': 'Unofficial',
                'Official results': 'Official',
              })}
            </div>
          </div>
        )}
      </div>

      {/* Race Info */}
      <div className={`section-block ${showRace ? 'expanded' : 'collapsed'}`}>
        <div className="section-title" onClick={() => setShowRace(!showRace)}>
          <span className="toggle-icon">{showRace ? '▼' : '▶'}</span>
          Race Info
        </div>
        {showRace && (
          <div className="section-content">
            <div className="fields-grid">
              {renderField('discipline', 'Discipline', 'select', {
                'Sprint': 'SP',
                'Distance': 'DI',
                'Team Relay': 'TE',
                'Team Sprint': 'Tsp',
                'Marathon': 'Mar',
              })}
              {renderField('format', 'Format', 'select', {
                'Individual Distance': 'individualDistance',
                'Sprint Qualification': 'sprintQualification',
                'Sprint Final': 'sprintFinal',
                'Team': 'team',
                'Team Sprint': 'teamSprint',
              })}
              {showUssaFields && renderField('eventType', 'Event Type', 'select', {
                'Mass Start': 'MS',
                'Interval Start': 'IS',
                'Qualifier': 'Qual',
                'Sprint Final': 'Final',
                'Pursuit': 'Pur',
              })}
              {renderField('raceDate', 'Race Date', 'date')}
              {renderField('technique', 'Technique', 'select', {
                'Freestyle': 'FR',
                'Classic': 'CL',
              })}
              {renderField('distance', 'Distance', 'text', null, 'e.g., 10km')}
            </div>
          </div>
        )}
      </div>

      {/* Course Info */}
      <div className={`section-block ${showCourse ? 'expanded' : 'collapsed'}`}>
        <div className="section-title" onClick={() => setShowCourse(!showCourse)}>
          <span className="toggle-icon">{showCourse ? '▼' : '▶'}</span>
          Course Info
        </div>
        {showCourse && (
          <div className="section-content">
            <div className="fields-grid">
              {showUssaFields && renderField('homologation', 'Homologation')}
              {renderField('lapLength', 'Lap Length')}
              {renderField('lapNumber', 'Lap Number', 'number')}
              {renderField('coordinator', 'Coordinator')}
              {renderField('tc', 'Total Climb (m)')}
              {renderField('hd', 'Height Difference (m)')}
              {renderField('mc', 'Maximum Climb (m)')}
            </div>
          </div>
        )}
      </div>

      {/* File Info */}
      <div className={`section-block ${showFile ? 'expanded' : 'collapsed'}`}>
        <div className="section-title" onClick={() => setShowFile(!showFile)}>
          <span className="toggle-icon">{showFile ? '▼' : '▶'}</span>
          File / Codex Info
        </div>
        {showFile && (
          <div className="section-content">
            <div className="fields-grid">
              {renderField('sex', 'Sex', 'select', {
                'Male': 'M',
                'Female': 'W',
                'Team': 'A',
              })}
              {fisEnabled && renderField('fisCodex', <><img src={fisLogo} alt="FIS" className="inline-fis-icon" /> FIS Codex</>, 'number')}
              {showUssaFields && renderField('ussaCode', '🇺🇸 USSA Race Code')}
              {renderField('startTime', 'Start Time (24h)', 'text', null, '10:00')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HeaderSummary;
